import React, { useEffect, useState } from "react";
import axios from "axios";

const AdminAwards = () => {
  const [awards, setAwards] = useState([]);
  const [form, setForm] = useState({
    title: "",
    year: "",
    category: "",
    image: null,
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchAwards();
  }, []);

  const fetchAwards = async () => {
    const res = await axios.get("https://archpoint.onrender.com/api/awards");
    setAwards(res.data);
  };

  const handleChange = (e) => {
    if (e.target.name === "image") {
      setForm({ ...form, image: e.target.files[0] });
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(form).forEach((key) => {
      if (form[key]) data.append(key, form[key]);
    });

    if (editingId) {
      await axios.put(`https://archpoint.onrender.com/api/awards/${editingId}`, data);
    } else {
      await axios.post("https://archpoint.onrender.com/api/awards", data);
    }

    setForm({ title: "", year: "", category: "", image: null });
    setEditingId(null);
    fetchAwards();
  };

  const handleEdit = (award) => {
    setEditingId(award._id);
    setForm({
      title: award.title,
      year: award.year,
      category: award.category,
      image: null,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this award?")) return;
    await axios.delete(`https://archpoint.onrender.com/api/awards/${id}`);
    fetchAwards();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-slate-800 mb-2">Awards Management</h2>
          <p className="text-slate-600">Manage and organize your awards collection</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl shadow-lg mb-10 border border-slate-200">
          <h3 className="text-xl font-semibold text-slate-800 mb-6">
            {editingId ? "Edit Award" : "Add New Award"}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Award Title</label>
              <input
                name="title"
                placeholder="Enter award title"
                value={form.title}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Year</label>
              <input
                name="year"
                placeholder="Enter year"
                value={form.year}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
              <input
                name="category"
                placeholder="Enter category"
                value={form.category}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Award Image</label>
              <input
                type="file"
                name="image"
                accept="image/*"
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100 file:cursor-pointer"
              />
            </div>
          </div>

          <button 
            type="submit"
            className="px-8 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-semibold rounded-lg shadow-md hover:from-yellow-600 hover:to-yellow-700 transition transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
          >
            {editingId ? "Update Award" : "Add Award"}
          </button>
        </form>

        {/* Awards Grid */}
        <div>
          <h3 className="text-2xl font-bold text-slate-800 mb-6">All Awards</h3>
          {awards.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-slate-200">
              <div className="text-slate-400 mb-2">
                <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <p className="text-slate-600 text-lg">No awards yet. Add your first award above!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {awards.map((award) => (
                <div key={award._id} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200 hover:shadow-xl transition transform hover:-translate-y-1">
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-6 flex items-center justify-center h-48">
                    <img
                      src={award.image.url}
                      alt={award.title}
                      className="h-full object-contain"
                    />
                  </div>
                  
                  <div className="p-6">
                    <h3 className="font-bold text-lg text-slate-800 mb-2">{award.title}</h3>
                    <div className="flex items-center text-slate-600 text-sm mb-4">
                      <span className="font-semibold">{award.year}</span>
                      <span className="mx-2">•</span>
                      <span>{award.category}</span>
                    </div>

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                      <button
                        onClick={() => handleEdit(award)}
                        className="flex-1 px-4 py-2 bg-blue-50 text-blue-600 font-medium rounded-lg hover:bg-blue-100 transition focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(award._id)}
                        className="flex-1 px-4 py-2 bg-red-50 text-red-600 font-medium rounded-lg hover:bg-red-100 transition focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAwards;